import { Trans, useLingui } from '@lingui/react/macro'
import * as v from 'valibot'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useAppForm } from '@/lib/use-app-form'
import { useMutation } from '@tanstack/react-query'
import { trpc } from '@/main'
import { toast } from '@/lib/toast'

/**
 * The static half of the contact schema lives at module scope so it is built once per process, not once
 * per render. Only `terms` needs the translated message, so that field is added by `contactSchema` inside
 * the form component (where `t` is available).
 */
const contactFields = {
  name: v.pipe(v.string(), v.minLength(1)),
  email: v.pipe(v.string(), v.email()),
  message: v.pipe(v.string(), v.minLength(1)),
}

function contactSchema(termsMessage: string) {
  return v.object({
    ...contactFields,
    terms: v.pipe(
      v.boolean(),
      v.check((val) => val, termsMessage),
    ),
  })
}

/**
 * Thin shell rendered on every landing page. The form itself lives in `ContactForm`, which sits inside
 * `DialogContent`: base-ui's `DialogPortal` renders nothing while the dialog is closed (it is not
 * `keepMounted`), so the form's hooks — the mutation, TanStack Form and the valibot schema — only run once
 * a visitor actually opens the dialog. Before this split they ran on every landing SSR and client render
 * of a dialog nobody had opened.
 */
export function ContactDialog(props: { openContact: boolean; setOpenContact: (value: boolean) => void }) {
  const { t } = useLingui()

  return (
    <Dialog open={props.openContact} onOpenChange={props.setOpenContact}>
      <DialogContent aria-describedby={undefined} className="max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="text-center text-2xl font-semibold">{t`Contact us`}</DialogTitle>
        </DialogHeader>

        <ContactForm />
      </DialogContent>
    </Dialog>
  )
}

function ContactForm() {
  const { t } = useLingui()
  const { mutateAsync: sendContactForm, isSuccess } = useMutation(trpc.common.sendContactForm.mutationOptions())

  const form = useAppForm({
    validators: {
      onChange: contactSchema(t`You must accept the privacy policy and terms of use`),
    },
    defaultValues: {
      name: '',
      email: '',
      message: '',
      terms: false,
    },
    onSubmit: async ({ value }) => {
      await sendContactForm(value)
      toast.success(t`Message sent successfully`)
    },
  })

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        form.handleSubmit()
      }}
      className="flex flex-col gap-3"
    >
      <form.AppField name="name" children={(field) => <field.TextField label={t`Name`} required />} />

      <form.AppField name="email" children={(field) => <field.TextField label={t`Email`} type="email" required />} />

      <form.AppField name="message" children={(field) => <field.TextareaField label={t`Message`} required />} />

      <form.AppField
        name="terms"
        children={(field) => (
          <field.CheckboxField
            label={
              <div>
                <Trans>
                  I accept the{' '}
                  <a
                    href="/terms-of-use"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Terms of use
                  </a>{' '}
                  and the{' '}
                  <a
                    href="/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Privacy Policy
                  </a>
                </Trans>
              </div>
            }
          />
        )}
      />

      <form.AppForm>
        <form.SubmitButton label={t`Send`} disabled={isSuccess} className="w-full" />
      </form.AppForm>
    </form>
  )
}
